import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  RotateCw, Clock, Calendar, BarChart2, Plus, Trash2,
  ChevronRight, Flame, Snowflake, Thermometer, AlertCircle
} from 'lucide-react';
import TabBar from '../components/TabBar';
import { API_BASE } from '../services/apiBase';

const API = API_BASE;
const TENANT = '569593dd-38e2-4d69-bdca-5d07be8b46f1';

const SLOT_TYPE_COLORS = {
  music:   '#22c55e',
  jingle:  '#3b82f6',
  ad:      '#f59e0b',
  sweeper: '#a78bfa',
  liner:   '#14b8a6',
  djlink:  '#f97316',
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_VALUES = [1, 2, 3, 4, 5, 6, 7];  // 1=Mon … 7=Sun, matching API integers

// ─── Utility helpers ────────────────────────────────────────────────────────

function getToken() {
  return localStorage.getItem('access_token') || '';
}

function authHeaders() {
  return { Authorization: 'Bearer ' + getToken() };
}

function timeAgo(isoString) {
  if (!isoString) return '—';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function trackHeat(lastPlayedAt, maxRepeatHours) {
  if (!lastPlayedAt) return 'cold';
  const hoursAgo = (Date.now() - new Date(lastPlayedAt).getTime()) / 3600000;
  if (hoursAgo <= maxRepeatHours) return 'hot';
  if (hoursAgo <= maxRepeatHours * 2) return 'warm';
  return 'cold';
}

function categoryHeat(tracks, maxRepeatHours) {
  if (!tracks || tracks.length === 0) return 'cold';
  let hot = 0, warm = 0;
  tracks.forEach(t => {
    const h = trackHeat(t.last_played_at, maxRepeatHours || 4);
    if (h === 'hot') hot++;
    else if (h === 'warm') warm++;
  });
  const ratio = hot / tracks.length;
  if (ratio > 0.4) return 'hot';
  if (warm + hot > 0) return 'warm';
  return 'cold';
}

function HeatDot({ level }) {
  const cls = level === 'hot' ? 'heat-dot heat-hot'
    : level === 'warm' ? 'heat-dot heat-warm'
    : 'heat-dot heat-cold';
  return <span className={cls} style={{ marginRight: 6 }} />;
}

function HeatBadge({ level }) {
  if (level === 'hot') return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11, fontWeight:700,
      background:'rgba(248,81,73,0.12)', color:'#f85149', borderRadius:4, padding:'2px 7px' }}>
      <Flame size={11} /> Hot
    </span>
  );
  if (level === 'warm') return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11, fontWeight:700,
      background:'rgba(227,179,65,0.12)', color:'#e3b341', borderRadius:4, padding:'2px 7px' }}>
      <Thermometer size={11} /> Warm
    </span>
  );
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11, fontWeight:700,
      background:'rgba(63,185,80,0.1)', color:'#3fb950', borderRadius:4, padding:'2px 7px' }}>
      <Snowflake size={11} /> Cold
    </span>
  );
}

function ErrorBanner({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, background:'rgba(248,81,73,0.1)',
      border:'1px solid rgba(248,81,73,0.3)', borderRadius:8, padding:'10px 16px', marginBottom:16,
      color:'#f85149', fontSize:13 }}>
      <AlertCircle size={15} />
      <span style={{ flex:1 }}>{message}</span>
      {onDismiss && (
        <button onClick={onDismiss} style={{ background:'none', border:'none', color:'#f85149',
          cursor:'pointer', fontSize:16, lineHeight:1 }}>×</button>
      )}
    </div>
  );
}

function LoadingSpinner({ label }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10,
      padding:48, color:'var(--text-secondary)', fontSize:14 }}>
      <RotateCw size={16} style={{ animation:'spin 1s linear infinite' }} />
      {label || 'Loading…'}
    </div>
  );
}

// ─── SVG Clock Wheel ────────────────────────────────────────────────────────

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx, cy, r, startDeg, endDeg) {
  const start = polarToCartesian(cx, cy, r, endDeg);
  const end = polarToCartesian(cx, cy, r, startDeg);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  return [
    'M', cx, cy,
    'L', start.x, start.y,
    'A', r, r, 0, largeArc, 0, end.x, end.y,
    'Z',
  ].join(' ');
}

function midAngle(start, end) {
  return (start + end) / 2;
}

function ClockWheel({ slots, categories }) {
  const SIZE = 320;
  const cx = SIZE / 2;
  const cy = SIZE / 2;
  const R = 130;
  const INNER_R = 44;

  if (!slots || slots.length === 0) {
    return (
      <svg width={SIZE} height={SIZE} style={{ display:'block', margin:'0 auto' }}>
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="var(--border)"
          strokeWidth={2} strokeDasharray="8 6" />
        <circle cx={cx} cy={cy} r={INNER_R} fill="var(--bg-elevated)" />
        <text x={cx} y={cy - 8} textAnchor="middle" fill="var(--text-muted)" fontSize={11}>
          No slots —
        </text>
        <text x={cx} y={cy + 8} textAnchor="middle" fill="var(--text-muted)" fontSize={11}>
          add slots below
        </text>
      </svg>
    );
  }

  const catMap = {};
  (categories || []).forEach(c => { catMap[c.id] = c; });

  // Duration-weighted arcs — fallback to equal slices if no durations set
  const totalDur = slots.reduce((s, sl) => s + (sl.duration_seconds || 180), 0);
  let cursor = 0;
  const slotArcs = slots.map(sl => {
    const dur = sl.duration_seconds || 180;
    const span = slots.length === 1 ? 359.99 : (dur / totalDur) * 360;
    const start = cursor;
    cursor += span;
    return { slot: sl, startDeg: start, endDeg: start + span, span };
  });

  return (
    <svg width={SIZE} height={SIZE} style={{ display:'block', margin:'0 auto' }}>
      {/* Hour tick marks */}
      {Array.from({ length: 12 }).map((_, i) => {
        const a = i * 30;
        const p1 = polarToCartesian(cx, cy, R + 6, a);
        const p2 = polarToCartesian(cx, cy, R + 14, a);
        return (
          <line key={i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
            stroke={i === 0 ? 'var(--accent)' : 'var(--border-bright)'}
            strokeWidth={i === 0 ? 2.5 : 1} />
        );
      })}
      {/* Hour labels */}
      {[12,3,6,9].map((h, i) => {
        const a = i * 90;
        const p = polarToCartesian(cx, cy, R + 22, a);
        return (
          <text key={h} x={p.x} y={p.y} textAnchor="middle" dominantBaseline="middle"
            fill="var(--text-muted)" fontSize={9} fontWeight={600}>{h}</text>
        );
      })}

      {slotArcs.map(({ slot, startDeg, endDeg, span }, i) => {
        const cat = catMap[slot.category_id] || {};
        const color = SLOT_TYPE_COLORS[slot.slot_type] || cat.color || '#8b949e';
        const mid = midAngle(startDeg, endDeg);
        const labelR = (R + INNER_R) / 2;
        const lp = polarToCartesian(cx, cy, labelR, mid);
        const label = (slot.label || cat.name || slot.slot_type || '').slice(0, 9);
        // Flip text in the lower half so it's never upside-down
        const textAngle = mid > 90 && mid < 270 ? mid + 180 : mid;

        return (
          <g key={slot.id || i}>
            <path
              d={describeArc(cx, cy, R, startDeg, endDeg)}
              fill={color}
              fillOpacity={0.82}
              stroke="var(--bg-base)"
              strokeWidth={2}
            />
            {span > 18 && (
              <text
                x={lp.x} y={lp.y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="slot-arc-label"
                transform={`rotate(${textAngle}, ${lp.x}, ${lp.y})`}
              >
                {label}
              </text>
            )}
          </g>
        );
      })}

      {/* Inner hub */}
      <circle cx={cx} cy={cy} r={INNER_R} fill="var(--bg-elevated)" stroke="var(--border)" strokeWidth={1.5} />
      <text x={cx} y={cy - 6} textAnchor="middle" fill="var(--text-secondary)" fontSize={10} fontWeight={700}>
        {slots.length}
      </text>
      <text x={cx} y={cy + 7} textAnchor="middle" fill="var(--text-muted)" fontSize={9}>
        slots
      </text>

      {/* 12 o'clock marker */}
      <line x1={cx} y1={cy - R - 2} x2={cx} y2={cy - R - 14}
        stroke="var(--accent)" strokeWidth={3} strokeLinecap="round" />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 1: Rotations (Categories + Tracks)
// ═══════════════════════════════════════════════════════════════════════════

function RotationsTab({ storeId }) {
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [library, setLibrary] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [loadingLib, setLoadingLib] = useState(false);
  const [error, setError] = useState(null);
  const [libSearch, setLibSearch] = useState('');
  const [addingTrack, setAddingTrack] = useState(null);
  const [autoFilling, setAutoFilling] = useState(false);

  // Reset selected category when store changes
  useEffect(() => { setSelectedCat(null); }, [storeId]);

  const fetchCategories = useCallback(async () => {
    setLoadingCats(true);
    setError(null);
    try {
      const params = `tenant_id=${TENANT}${storeId ? `&store_id=${storeId}` : ''}`;
      const res = await axios.get(`${API}/rotations/categories?${params}`, { headers: authHeaders() });
      setCategories(res.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load categories');
    } finally {
      setLoadingCats(false);
    }
  }, [storeId]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const fetchTracks = useCallback(async (catId) => {
    setLoadingTracks(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/rotations/categories/${catId}/tracks?tenant_id=${TENANT}`, { headers: authHeaders() });
      setTracks(res.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load tracks');
      setTracks([]);
    } finally {
      setLoadingTracks(false);
    }
  }, []);

  const fetchLibrary = useCallback(async (search) => {
    setLoadingLib(true);
    try {
      const params = new URLSearchParams({ tenant_id: TENANT, track_type: 'music' });
      if (search) params.set('search', search);
      const res = await axios.get(`${API}/music/tracks?${params}`, { headers: authHeaders() });
      setLibrary(res.data?.items || res.data || []);
    } catch (e) {
      setLibrary([]);
    } finally {
      setLoadingLib(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCat) {
      fetchTracks(selectedCat.id);
      fetchLibrary(libSearch);
    }
  }, [selectedCat]);

  const handleLibSearch = (val) => {
    setLibSearch(val);
    fetchLibrary(val);
  };

  const handleAddTrack = async (track) => {
    if (!selectedCat) return;
    setAddingTrack(track.id);
    try {
      await axios.post(`${API}/rotations/categories/${selectedCat.id}/tracks`,
        { track_id: track.id, tenant_id: TENANT },
        { headers: authHeaders() }
      );
      await fetchTracks(selectedCat.id);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to add track');
    } finally {
      setAddingTrack(null);
    }
  };

  const handleRemoveTrack = async (trackId) => {
    if (!selectedCat) return;
    try {
      await axios.delete(`${API}/rotations/categories/${selectedCat.id}/tracks/${trackId}`,
        { headers: authHeaders() }
      );
      setTracks(t => t.filter(x => x.id !== trackId));
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to remove track');
    }
  };

  const handleAutoFill = async () => {
    if (!selectedCat) return;
    setAutoFilling(true);
    try {
      await axios.post(`${API}/rotations/categories/${selectedCat.id}/auto-fill`,
        { tenant_id: TENANT },
        { headers: authHeaders() }
      );
      await fetchTracks(selectedCat.id);
    } catch (e) {
      setError(e.response?.data?.detail || 'Auto-fill failed');
    } finally {
      setAutoFilling(false);
    }
  };

  const maxRepeatHours = selectedCat?.max_repeat_hours || 4;

  return (
    <div>
      <ErrorBanner message={error} onDismiss={() => setError(null)} />

      <div className="rotation-grid">
        {/* Left: Category list */}
        <div style={{ borderRight:'1px solid var(--border)', overflowY:'auto', background:'var(--bg-surface)' }}>
          <div style={{ padding:'12px 14px', borderBottom:'1px solid var(--border)',
            display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span style={{ fontSize:12, fontWeight:700, color:'var(--text-secondary)', letterSpacing:'0.06em', textTransform:'uppercase' }}>
              Categories
            </span>
            <span style={{ fontSize:11, color:'var(--text-muted)' }}>{categories.length}</span>
          </div>

          {loadingCats ? (
            <LoadingSpinner label="Loading categories…" />
          ) : categories.length === 0 ? (
            <div style={{ padding:24, color:'var(--text-muted)', fontSize:13, textAlign:'center' }}>
              No categories found
            </div>
          ) : categories.map(cat => {
            const heat = categoryHeat(cat.tracks || [], cat.max_repeat_hours || 4);
            const isSelected = selectedCat?.id === cat.id;
            return (
              <div
                key={cat.id}
                onClick={() => setSelectedCat(cat)}
                style={{
                  display:'flex', alignItems:'center', gap:10, padding:'10px 14px',
                  cursor:'pointer', borderBottom:'1px solid var(--border)',
                  background: isSelected ? 'var(--bg-hover)' : 'transparent',
                  borderLeft: isSelected ? '3px solid var(--accent)' : '3px solid transparent',
                  transition:'background 0.1s',
                }}
              >
                <span style={{
                  width:10, height:10, borderRadius:'50%', flexShrink:0,
                  background: cat.color || 'var(--accent)',
                  boxShadow: isSelected ? `0 0 6px ${cat.color || 'var(--accent)'}` : 'none',
                }} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:isSelected ? 700 : 500,
                    color: isSelected ? 'var(--text-primary)' : 'var(--text-secondary)',
                    whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {cat.name}
                  </div>
                  <div style={{ fontSize:11, color:'var(--text-muted)' }}>
                    {cat.track_count ?? '?'} tracks
                  </div>
                </div>
                <HeatBadge level={heat} />
              </div>
            );
          })}
        </div>

        {/* Center: Track list */}
        <div style={{ overflowY:'auto', background:'var(--bg-base)' }}>
          {!selectedCat ? (
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
              height:'100%', color:'var(--text-muted)', gap:10, fontSize:14 }}>
              <ChevronRight size={28} />
              <span>Select a category</span>
            </div>
          ) : (
            <>
              <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)',
                display:'flex', alignItems:'center', justifyContent:'space-between',
                background:'var(--bg-surface)', position:'sticky', top:0, zIndex:2 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ width:10, height:10, borderRadius:'50%',
                    background: selectedCat.color || 'var(--accent)' }} />
                  <span style={{ fontSize:14, fontWeight:700, color:'var(--text-primary)' }}>
                    {selectedCat.name}
                  </span>
                  <span style={{ fontSize:12, color:'var(--text-muted)' }}>
                    {tracks.length} tracks
                  </span>
                </div>
                <button className="btn" onClick={handleAutoFill} disabled={autoFilling}
                  style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}>
                  <RotateCw size={13} style={{ animation: autoFilling ? 'spin 1s linear infinite' : 'none' }} />
                  Auto-Fill
                </button>
              </div>

              {loadingTracks ? (
                <LoadingSpinner label="Loading tracks…" />
              ) : tracks.length === 0 ? (
                <div style={{ padding:32, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>
                  No tracks in this category
                </div>
              ) : (
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-surface)' }}>
                      {['#','Title','Artist','BPM','Dur.','Last Played','Heat',''].map((h, i) => (
                        <th key={i} style={{ padding:'8px 12px', textAlign:'left', fontWeight:600,
                          color:'var(--text-muted)', letterSpacing:'0.04em', fontSize:11,
                          textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tracks.map((t, idx) => {
                      const heat = trackHeat(t.last_played_at, maxRepeatHours);
                      return (
                        <tr key={t.id} style={{ borderBottom:'1px solid var(--border)',
                          transition:'background 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={{ padding:'8px 12px', color:'var(--text-muted)', fontFamily:'var(--mono)' }}>
                            {idx + 1}
                          </td>
                          <td style={{ padding:'8px 12px', color:'var(--text-primary)', fontWeight:500,
                            maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {t.title}
                          </td>
                          <td style={{ padding:'8px 12px', color:'var(--text-secondary)',
                            maxWidth:130, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {t.artist}
                          </td>
                          <td style={{ padding:'8px 12px', color:'var(--text-muted)', fontFamily:'var(--mono)' }}>
                            {t.bpm || '—'}
                          </td>
                          <td style={{ padding:'8px 12px', color:'var(--text-muted)', fontFamily:'var(--mono)', whiteSpace:'nowrap' }}>
                            {t.duration ? `${Math.floor(t.duration/60)}:${String(t.duration%60).padStart(2,'0')}` : '—'}
                          </td>
                          <td style={{ padding:'8px 12px', color:'var(--text-muted)', whiteSpace:'nowrap' }}>
                            {timeAgo(t.last_played_at)}
                          </td>
                          <td style={{ padding:'8px 12px' }}>
                            <HeatDot level={heat} />
                          </td>
                          <td style={{ padding:'8px 8px', textAlign:'right' }}>
                            <button onClick={() => handleRemoveTrack(t.id)}
                              style={{ background:'none', border:'none', cursor:'pointer',
                                color:'var(--text-muted)', padding:4, borderRadius:4,
                                display:'inline-flex', alignItems:'center' }}
                              onMouseEnter={e => e.currentTarget.style.color = '#f85149'}
                              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                            >
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        {/* Right: Library panel */}
        <div style={{ borderLeft:'1px solid var(--border)', overflowY:'auto', background:'var(--bg-surface)' }}>
          <div style={{ padding:'12px 14px', borderBottom:'1px solid var(--border)' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--text-secondary)',
              letterSpacing:'0.06em', textTransform:'uppercase', marginBottom:8 }}>
              Add from Library
            </div>
            <input
              value={libSearch}
              onChange={e => handleLibSearch(e.target.value)}
              placeholder="Search tracks…"
              style={{ width:'100%', boxSizing:'border-box', background:'var(--bg-base)',
                border:'1px solid var(--border)', borderRadius:6, padding:'7px 10px',
                color:'var(--text-primary)', fontSize:12, outline:'none' }}
            />
          </div>

          {!selectedCat ? (
            <div style={{ padding:20, color:'var(--text-muted)', fontSize:12, textAlign:'center' }}>
              Select a category first
            </div>
          ) : loadingLib ? (
            <LoadingSpinner label="Loading library…" />
          ) : library.length === 0 ? (
            <div style={{ padding:20, color:'var(--text-muted)', fontSize:12, textAlign:'center' }}>
              No tracks found
            </div>
          ) : library.map(track => {
            const alreadyIn = tracks.some(t => t.id === track.id);
            return (
              <div key={track.id} style={{ padding:'9px 14px', borderBottom:'1px solid var(--border)',
                display:'flex', alignItems:'center', gap:8, fontSize:12,
                opacity: alreadyIn ? 0.45 : 1 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ color:'var(--text-primary)', fontWeight:500,
                    whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {track.title}
                  </div>
                  <div style={{ color:'var(--text-muted)', fontSize:11,
                    whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {track.artist}
                  </div>
                </div>
                <button
                  disabled={alreadyIn || addingTrack === track.id}
                  onClick={() => handleAddTrack(track)}
                  style={{ background: alreadyIn ? 'var(--bg-hover)' : 'var(--accent)',
                    border:'none', borderRadius:5, padding:'4px 8px', cursor: alreadyIn ? 'default' : 'pointer',
                    color:'#fff', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', gap:4,
                    opacity: addingTrack === track.id ? 0.6 : 1 }}>
                  <Plus size={11} />
                  {alreadyIn ? 'In' : 'Add'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 2: Clock Wheel
// ═══════════════════════════════════════════════════════════════════════════

function ClockWheelTab({ storeId }) {
  const [clocks, setClocks] = useState([]);
  const [selectedClockId, setSelectedClockId] = useState('');
  const [slots, setSlots] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState(null);
  const [newClockName, setNewClockName] = useState('');
  const [creatingClock, setCreatingClock] = useState(false);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [newSlot, setNewSlot] = useState({ category_id:'', slot_type:'music', label:'' });
  const [addingSlot, setAddingSlot] = useState(false);
  const [deletingSlot, setDeletingSlot] = useState(null);

  useEffect(() => { setSelectedClockId(''); setSlots([]); }, [storeId]);

  const fetchClocks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sp = `tenant_id=${TENANT}${storeId ? `&store_id=${storeId}` : ''}`;
      const [clockRes, catRes] = await Promise.all([
        axios.get(`${API}/rotations/clocks?${sp}`, { headers: authHeaders() }),
        axios.get(`${API}/rotations/categories?${sp}`, { headers: authHeaders() }),
      ]);
      const cl = clockRes.data || [];
      setClocks(cl);
      setCategories(catRes.data || []);
      if (cl.length > 0 && !selectedClockId) {
        setSelectedClockId(cl[0].id);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load clocks');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => { fetchClocks(); }, [fetchClocks]);

  const fetchSlots = useCallback(async (clockId) => {
    if (!clockId) return;
    setLoadingSlots(true);
    try {
      const res = await axios.get(`${API}/rotations/clocks/${clockId}/slots`, { headers: authHeaders() });
      setSlots((res.data || []).sort((a, b) => (a.position || 0) - (b.position || 0)));
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load slots');
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (selectedClockId) fetchSlots(selectedClockId);
    else setSlots([]);
  }, [selectedClockId, fetchSlots]);

  const handleCreateClock = async () => {
    if (!newClockName.trim()) return;
    setCreatingClock(true);
    try {
      const res = await axios.post(`${API}/rotations/clocks`,
        { name: newClockName.trim(), tenant_id: TENANT, store_id: storeId || null },
        { headers: authHeaders() }
      );
      setClocks(c => [...c, res.data]);
      setSelectedClockId(res.data.id);
      setNewClockName('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create clock');
    } finally {
      setCreatingClock(false);
    }
  };

  const handleAddSlot = async () => {
    if (!selectedClockId || !newSlot.slot_type) return;
    setAddingSlot(true);
    try {
      const res = await axios.post(`${API}/rotations/clocks/${selectedClockId}/slots`,
        { ...newSlot, tenant_id: TENANT, position: slots.length },
        { headers: authHeaders() }
      );
      setSlots(s => [...s, res.data]);
      setNewSlot({ category_id:'', slot_type:'music', label:'' });
      setShowAddSlot(false);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to add slot');
    } finally {
      setAddingSlot(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!selectedClockId) return;
    setDeletingSlot(slotId);
    try {
      await axios.delete(`${API}/rotations/clocks/${selectedClockId}/slots/${slotId}`,
        { headers: authHeaders() }
      );
      setSlots(s => s.filter(x => x.id !== slotId));
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete slot');
    } finally {
      setDeletingSlot(null);
    }
  };

  const selectedClock = clocks.find(c => c.id === selectedClockId);
  const catMap = {};
  categories.forEach(c => { catMap[c.id] = c; });

  if (loading) return <LoadingSpinner label="Loading clock templates…" />;

  return (
    <div>
      <ErrorBanner message={error} onDismiss={() => setError(null)} />

      {/* Clock selector + create */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24,
        flexWrap:'wrap' }}>
        <select
          value={selectedClockId}
          onChange={e => setSelectedClockId(e.target.value)}
          style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:7,
            padding:'8px 12px', color:'var(--text-primary)', fontSize:13, minWidth:200, outline:'none' }}>
          <option value="">— Select clock template —</option>
          {clocks.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <div style={{ display:'flex', gap:6, flex:1 }}>
          <input
            value={newClockName}
            onChange={e => setNewClockName(e.target.value)}
            placeholder="New template name…"
            onKeyDown={e => e.key === 'Enter' && handleCreateClock()}
            style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:7,
              padding:'8px 12px', color:'var(--text-primary)', fontSize:13, flex:1, outline:'none' }}
          />
          <button className="btn" onClick={handleCreateClock} disabled={creatingClock || !newClockName.trim()}
            style={{ display:'flex', alignItems:'center', gap:6, whiteSpace:'nowrap' }}>
            <Plus size={14} /> New Clock
          </button>
        </div>
      </div>

      {!selectedClockId ? (
        <div style={{ textAlign:'center', padding:48, color:'var(--text-muted)', fontSize:14 }}>
          Select or create a clock template to get started
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 380px', gap:32, alignItems:'start' }}>
          {/* Left: Wheel */}
          <div>
            <div style={{ background:'var(--bg-surface)', borderRadius:12,
              border:'1px solid var(--border)', padding:28, textAlign:'center' }}>
              <div style={{ fontSize:13, fontWeight:700, color:'var(--text-secondary)',
                marginBottom:20, letterSpacing:'0.06em', textTransform:'uppercase' }}>
                {selectedClock?.name || 'Clock'}
              </div>
              {loadingSlots ? (
                <LoadingSpinner label="Loading slots…" />
              ) : (
                <ClockWheel slots={slots} categories={categories} />
              )}
            </div>

            {/* Slot type legend */}
            <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginTop:14, justifyContent:'center' }}>
              {Object.entries(SLOT_TYPE_COLORS).map(([type, color]) => (
                <span key={type} style={{ display:'flex', alignItems:'center', gap:5,
                  fontSize:11, color:'var(--text-secondary)' }}>
                  <span style={{ width:8, height:8, borderRadius:2, background:color, display:'inline-block' }} />
                  {type}
                </span>
              ))}
            </div>
          </div>

          {/* Right: Slot list */}
          <div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
              <span style={{ fontSize:13, fontWeight:700, color:'var(--text-secondary)',
                letterSpacing:'0.06em', textTransform:'uppercase' }}>
                Slots ({slots.length})
              </span>
              <button className="btn" onClick={() => setShowAddSlot(v => !v)}
                style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}>
                <Plus size={13} /> Add Slot
              </button>
            </div>

            {/* Add slot form */}
            {showAddSlot && (
              <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)',
                borderRadius:10, padding:16, marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'var(--text-secondary)',
                  marginBottom:12, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  New Slot
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  <select
                    value={newSlot.category_id}
                    onChange={e => setNewSlot(s => ({ ...s, category_id: e.target.value }))}
                    style={{ background:'var(--bg-base)', border:'1px solid var(--border)', borderRadius:6,
                      padding:'7px 10px', color:'var(--text-primary)', fontSize:12, outline:'none' }}>
                    <option value="">No category</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <select
                    value={newSlot.slot_type}
                    onChange={e => setNewSlot(s => ({ ...s, slot_type: e.target.value }))}
                    style={{ background:'var(--bg-base)', border:'1px solid var(--border)', borderRadius:6,
                      padding:'7px 10px', color:'var(--text-primary)', fontSize:12, outline:'none' }}>
                    {Object.keys(SLOT_TYPE_COLORS).map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <input
                    value={newSlot.label}
                    onChange={e => setNewSlot(s => ({ ...s, label: e.target.value }))}
                    placeholder="Label (optional)"
                    style={{ background:'var(--bg-base)', border:'1px solid var(--border)', borderRadius:6,
                      padding:'7px 10px', color:'var(--text-primary)', fontSize:12, outline:'none' }}
                  />
                  <div style={{ display:'flex', gap:8 }}>
                    <button className="btn" onClick={handleAddSlot} disabled={addingSlot}
                      style={{ flex:1, justifyContent:'center', display:'flex', alignItems:'center', gap:6, fontSize:12 }}>
                      <Plus size={12} /> {addingSlot ? 'Adding…' : 'Add Slot'}
                    </button>
                    <button onClick={() => setShowAddSlot(false)}
                      style={{ background:'none', border:'1px solid var(--border)', borderRadius:6,
                        padding:'7px 14px', color:'var(--text-muted)', cursor:'pointer', fontSize:12 }}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Slot rows */}
            {slots.length === 0 ? (
              <div style={{ textAlign:'center', padding:32, color:'var(--text-muted)', fontSize:13,
                border:'1px dashed var(--border)', borderRadius:10 }}>
                No slots yet
              </div>
            ) : slots.map((slot, i) => {
              const cat = catMap[slot.category_id] || {};
              const typeColor = SLOT_TYPE_COLORS[slot.slot_type] || '#8b949e';
              return (
                <div key={slot.id} style={{ display:'flex', alignItems:'center', gap:10,
                  padding:'9px 12px', borderRadius:8, marginBottom:4,
                  background:'var(--bg-surface)', border:'1px solid var(--border)',
                  fontSize:12 }}>
                  <span style={{ fontFamily:'var(--mono)', color:'var(--text-muted)',
                    fontSize:11, minWidth:18, textAlign:'right' }}>
                    {i + 1}
                  </span>
                  <span style={{ width:8, height:8, borderRadius:2, background:typeColor, flexShrink:0 }} />
                  <span style={{ flex:1, color:'var(--text-primary)', fontWeight:500,
                    whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {slot.label || cat.name || '—'}
                  </span>
                  <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:4,
                    background:`${typeColor}22`, color:typeColor, textTransform:'uppercase',
                    letterSpacing:'0.05em', flexShrink:0 }}>
                    {slot.slot_type}
                  </span>
                  <button
                    onClick={() => handleDeleteSlot(slot.id)}
                    disabled={deletingSlot === slot.id}
                    style={{ background:'none', border:'none', cursor:'pointer',
                      color:'var(--text-muted)', padding:4, borderRadius:4,
                      display:'inline-flex', alignItems:'center', flexShrink:0 }}
                    onMouseEnter={e => e.currentTarget.style.color = '#f85149'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 3: Schedules
// ═══════════════════════════════════════════════════════════════════════════

const EMPTY_SCHEDULE = {
  name: '',
  clock_id: '',
  days_of_week: [],
  start_time: '06:00',
  end_time: '18:00',
  date_from: '',
  date_to: '',
  priority: 1,
};

function SchedulesTab({ storeId }) {
  const [schedules, setSchedules] = useState([]);
  const [clocks, setClocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_SCHEDULE);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sp = `tenant_id=${TENANT}${storeId ? `&store_id=${storeId}` : ''}`;
      const [schRes, clkRes] = await Promise.all([
        axios.get(`${API}/rotations/schedules?${sp}`, { headers: authHeaders() }),
        axios.get(`${API}/rotations/clocks?${sp}`, { headers: authHeaders() }),
      ]);
      setSchedules(schRes.data || []);
      setClocks(clkRes.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Schedule name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form, tenant_id: TENANT, store_id: storeId || null };
      if (editingId) {
        await axios.patch(`${API}/rotations/schedules/${editingId}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/rotations/schedules`, payload, { headers: authHeaders() });
      }
      await fetchAll();
      setShowForm(false);
      setForm(EMPTY_SCHEDULE);
      setEditingId(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (sch) => {
    setForm({
      name: sch.name || '',
      clock_id: sch.clock_id || '',
      days_of_week: sch.days_of_week || [],
      start_time: sch.start_time || '06:00',
      end_time: sch.end_time || '18:00',
      date_from: sch.date_from || '',
      date_to: sch.date_to || '',
      priority: sch.priority || 1,
    });
    setEditingId(sch.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/rotations/schedules/${id}`, { headers: authHeaders() });
      setSchedules(s => s.filter(x => x.id !== id));
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete schedule');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleDay = (day) => {
    setForm(f => ({
      ...f,
      days_of_week: f.days_of_week.includes(day)
        ? f.days_of_week.filter(d => d !== day)
        : [...f.days_of_week, day],
    }));
  };

  const clockMap = {};
  clocks.forEach(c => { clockMap[c.id] = c; });

  if (loading) return <LoadingSpinner label="Loading schedules…" />;

  return (
    <div>
      <ErrorBanner message={error} onDismiss={() => setError(null)} />

      {/* Toolbar */}
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:20 }}>
        <button className="btn" onClick={() => { setShowForm(v => !v); setEditingId(null); setForm(EMPTY_SCHEDULE); }}
          style={{ display:'flex', alignItems:'center', gap:6 }}>
          <Plus size={14} /> New Schedule
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)',
          borderRadius:12, padding:24, marginBottom:28 }}>
          <div style={{ fontSize:14, fontWeight:700, color:'var(--text-primary)', marginBottom:20 }}>
            {editingId ? 'Edit Schedule' : 'New Schedule'}
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            <div>
              <label style={labelStyle}>Name</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Weekday Morning"
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Clock Template</label>
              <select value={form.clock_id} onChange={e => setForm(f => ({ ...f, clock_id: e.target.value }))}
                style={inputStyle}>
                <option value="">— None —</option>
                {clocks.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Start Time</label>
              <input type="time" value={form.start_time}
                onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>End Time</label>
              <input type="time" value={form.end_time}
                onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Date From (optional)</label>
              <input type="date" value={form.date_from}
                onChange={e => setForm(f => ({ ...f, date_from: e.target.value }))}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Date To (optional)</label>
              <input type="date" value={form.date_to}
                onChange={e => setForm(f => ({ ...f, date_to: e.target.value }))}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Priority</label>
              <input type="number" min={1} max={100} value={form.priority}
                onChange={e => setForm(f => ({ ...f, priority: parseInt(e.target.value) || 1 }))}
                style={inputStyle} />
            </div>
          </div>

          <div style={{ marginBottom:20 }}>
            <label style={labelStyle}>Days of Week</label>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {DAYS.map((day, i) => {
                const val = DAY_VALUES[i];
                const active = form.days_of_week.includes(val);
                return (
                  <button key={day} onClick={() => toggleDay(val)}
                    style={{ padding:'6px 14px', borderRadius:6, border:'1px solid',
                      borderColor: active ? 'var(--accent)' : 'var(--border)',
                      background: active ? 'var(--accent-glow)' : 'var(--bg-base)',
                      color: active ? 'var(--accent-bright)' : 'var(--text-secondary)',
                      fontWeight: active ? 700 : 400, cursor:'pointer', fontSize:13,
                      transition:'all 0.15s' }}>
                    {day}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button className="btn" onClick={handleSave} disabled={saving}
              style={{ display:'flex', alignItems:'center', gap:6 }}>
              {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Schedule'}
            </button>
            <button onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_SCHEDULE); }}
              style={{ background:'none', border:'1px solid var(--border)', borderRadius:7,
                padding:'8px 16px', color:'var(--text-muted)', cursor:'pointer', fontSize:13 }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Schedule list */}
      {schedules.length === 0 ? (
        <div style={{ textAlign:'center', padding:48, color:'var(--text-muted)', fontSize:14,
          border:'1px dashed var(--border)', borderRadius:12 }}>
          No schedules yet — create one above
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {schedules.map(sch => {
            const clock = clockMap[sch.clock_id];
            const days = (sch.days_of_week || []).map(d => DAYS[(Number(d) - 1) % 7] || d).join(', ');
            return (
              <div key={sch.id} style={{ background:'var(--bg-surface)', border:'1px solid var(--border)',
                borderRadius:10, padding:'14px 18px', display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:'var(--text-primary)', marginBottom:4 }}>
                    {sch.name}
                  </div>
                  <div style={{ display:'flex', gap:14, flexWrap:'wrap', fontSize:12, color:'var(--text-secondary)' }}>
                    {clock && (
                      <span style={{ display:'flex', alignItems:'center', gap:5 }}>
                        <Clock size={11} /> {clock.name}
                      </span>
                    )}
                    <span style={{ display:'flex', alignItems:'center', gap:5 }}>
                      <Calendar size={11} /> {days || 'No days'}
                    </span>
                    <span>{sch.start_time} – {sch.end_time}</span>
                    {(sch.date_from || sch.date_to) && (
                      <span style={{ color:'var(--text-muted)' }}>
                        {sch.date_from || '…'} → {sch.date_to || '…'}
                      </span>
                    )}
                    <span style={{ color:'var(--text-muted)' }}>Priority {sch.priority || 1}</span>
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                  <button onClick={() => handleEdit(sch)}
                    style={{ background:'none', border:'1px solid var(--border)', borderRadius:6,
                      padding:'5px 12px', color:'var(--text-secondary)', cursor:'pointer', fontSize:12 }}>
                    Edit
                  </button>
                  <button onClick={() => handleDelete(sch.id)} disabled={deletingId === sch.id}
                    style={{ background:'none', border:'1px solid rgba(248,81,73,0.3)', borderRadius:6,
                      padding:'5px 10px', color:'#f85149', cursor:'pointer', display:'inline-flex',
                      alignItems:'center', gap:5, fontSize:12 }}>
                    <Trash2 size={12} />
                    {deletingId === sch.id ? '…' : 'Delete'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const labelStyle = {
  display:'block', fontSize:11, fontWeight:600, color:'var(--text-muted)',
  textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6,
};
const inputStyle = {
  width:'100%', boxSizing:'border-box', background:'var(--bg-base)',
  border:'1px solid var(--border)', borderRadius:7, padding:'8px 12px',
  color:'var(--text-primary)', fontSize:13, outline:'none',
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB 4: Analysis
// ═══════════════════════════════════════════════════════════════════════════

const HOUR_OPTIONS = [
  { label: '6h',     value: 6 },
  { label: '12h',    value: 12 },
  { label: '24h',    value: 24 },
  { label: '7 days', value: 168 },
];

function AnalysisTab({ storeId }) {
  const [hours, setHours] = useState(24);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReport = useCallback(async (h) => {
    setLoading(true);
    setError(null);
    try {
      const storePart = storeId ? `&store_id=${storeId}` : '';
      const res = await axios.get(
        `${API}/broadcast-queue/spin-report?tenant_id=${TENANT}&hours=${h}${storePart}`,
        { headers: authHeaders() }
      );
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load spin report');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => { fetchReport(hours); }, [hours, fetchReport, storeId]);

  const tracks = data?.top_tracks || [];
  const unplayed = data?.unplayed || [];
  const totalSpins = data?.total_spins ?? tracks.reduce((s, t) => s + (t.spin_count || 0), 0);
  const uniqueTracks = data?.unique_tracks_played ?? tracks.length;
  const mostSpun = tracks.reduce((best, t) => (!best || (t.spin_count || 0) > (best.spin_count || 0)) ? t : best, null);

  function spinHeat(track) {
    if (!track.last_played_at) return 'cold';
    const hAgo = (Date.now() - new Date(track.last_played_at).getTime()) / 3600000;
    const maxH = track.max_repeat_hours || 4;
    if (hAgo <= maxH) return 'hot';
    if (hAgo <= maxH * 2) return 'warm';
    return 'cold';
  }

  const statCards = [
    { label:'Total Spins (period)', value: totalSpins ?? '—', icon:<BarChart2 size={18} /> },
    { label:'Unique Tracks', value: uniqueTracks ?? '—', icon:<RotateCw size={18} /> },
    { label:'Unplayed Tracks', value: unplayed.length, icon:<AlertCircle size={18} /> },
    { label:'Most Spun', value: mostSpun ? `${mostSpun.artist} – ${mostSpun.title}` : '—',
      sub: mostSpun ? `${mostSpun.spin_count} spins` : '',
      icon:<Flame size={18} /> },
  ];

  return (
    <div>
      <ErrorBanner message={error} onDismiss={() => setError(null)} />

      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:24 }}>
        {statCards.map((s, i) => (
          <div key={i} style={{ background:'var(--bg-surface)', border:'1px solid var(--border)',
            borderRadius:10, padding:'16px 18px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, color:'var(--accent)',
              marginBottom:8 }}>
              {s.icon}
              <span style={{ fontSize:11, fontWeight:600, color:'var(--text-muted)',
                textTransform:'uppercase', letterSpacing:'0.06em' }}>
                {s.label}
              </span>
            </div>
            <div style={{ fontSize: typeof s.value === 'string' && s.value.length > 20 ? 12 : 22,
              fontWeight:700, color:'var(--text-primary)', lineHeight:1.2,
              whiteSpace: typeof s.value === 'string' ? 'normal' : 'nowrap',
              overflow:'hidden', textOverflow:'ellipsis' }}>
              {s.value}
            </div>
            {s.sub && (
              <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:3 }}>{s.sub}</div>
            )}
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
        <span style={{ fontSize:12, color:'var(--text-muted)', fontWeight:600 }}>Period:</span>
        {HOUR_OPTIONS.map(o => (
          <button key={o.value} onClick={() => setHours(o.value)}
            style={{ padding:'6px 14px', borderRadius:6, border:'1px solid',
              borderColor: hours === o.value ? 'var(--accent)' : 'var(--border)',
              background: hours === o.value ? 'var(--accent-glow)' : 'var(--bg-surface)',
              color: hours === o.value ? 'var(--accent-bright)' : 'var(--text-secondary)',
              fontWeight: hours === o.value ? 700 : 400, cursor:'pointer', fontSize:13,
              transition:'all 0.15s' }}>
            {o.label}
          </button>
        ))}
      </div>

      {/* Spin table */}
      <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)',
        borderRadius:12, overflow:'hidden', marginBottom:28 }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border)',
          fontSize:13, fontWeight:700, color:'var(--text-primary)' }}>
          Spin Report
        </div>

        {loading ? (
          <LoadingSpinner label="Loading spin report…" />
        ) : tracks.length === 0 ? (
          <div style={{ padding:32, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>
            No spin data for this period
          </div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-elevated)' }}>
                {['#','Artist – Title','Category','Spins','Last Played','Avg Gap','Heat'].map((h, i) => (
                  <th key={i} style={{ padding:'9px 14px', textAlign:'left', fontWeight:600,
                    color:'var(--text-muted)', letterSpacing:'0.04em', fontSize:11,
                    textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tracks.map((t, idx) => {
                const heat = spinHeat(t);
                return (
                  <tr key={t.id || idx} style={{ borderBottom:'1px solid var(--border)',
                    transition:'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding:'9px 14px', color:'var(--text-muted)', fontFamily:'var(--mono)' }}>
                      {idx + 1}
                    </td>
                    <td style={{ padding:'9px 14px', color:'var(--text-primary)', fontWeight:500 }}>
                      {t.track_artist} – {t.track_title}
                    </td>
                    <td style={{ padding:'9px 14px', color:'var(--text-secondary)' }}>
                      {t.category_name || '—'}
                    </td>
                    <td style={{ padding:'9px 14px', fontFamily:'var(--mono)', fontWeight:700,
                      color:'var(--accent-bright)' }}>
                      {t.spin_count ?? 0}
                    </td>
                    <td style={{ padding:'9px 14px', color:'var(--text-muted)', whiteSpace:'nowrap' }}>
                      {timeAgo(t.last_played_at)}
                    </td>
                    <td style={{ padding:'9px 14px', color:'var(--text-muted)', fontFamily:'var(--mono)', whiteSpace:'nowrap' }}>
                      {t.avg_gap_minutes != null ? `${Math.round(t.avg_gap_minutes)}m` : '—'}
                    </td>
                    <td style={{ padding:'9px 14px' }}>
                      <HeatBadge level={heat} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Unplayed tracks section */}
      {!loading && unplayed.length > 0 && (
        <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)',
          borderRadius:12, overflow:'hidden' }}>
          <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border)',
            display:'flex', alignItems:'center', gap:8 }}>
            <AlertCircle size={14} style={{ color:'var(--yellow)' }} />
            <span style={{ fontSize:13, fontWeight:700, color:'var(--text-primary)' }}>
              Unplayed Tracks
            </span>
            <span style={{ fontSize:12, color:'var(--text-muted)' }}>
              ({unplayed.length} tracks in rotation with zero spins in period)
            </span>
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-elevated)' }}>
                {['#','Artist – Title','Category','Last Played'].map((h, i) => (
                  <th key={i} style={{ padding:'9px 14px', textAlign:'left', fontWeight:600,
                    color:'var(--text-muted)', letterSpacing:'0.04em', fontSize:11,
                    textTransform:'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {unplayed.map((t, idx) => (
                <tr key={t.id || idx} style={{ borderBottom:'1px solid var(--border)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding:'9px 14px', color:'var(--text-muted)', fontFamily:'var(--mono)' }}>
                    {idx + 1}
                  </td>
                  <td style={{ padding:'9px 14px', color:'var(--text-primary)', fontWeight:500 }}>
                    {t.artist} – {t.title}
                  </td>
                  <td style={{ padding:'9px 14px', color:'var(--text-secondary)' }}>
                    {t.category_name || '—'}
                  </td>
                  <td style={{ padding:'9px 14px', color:'var(--text-muted)' }}>
                    {t.last_played_at ? timeAgo(t.last_played_at) : 'Never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ROOT: RotationManager
// ═══════════════════════════════════════════════════════════════════════════

const TABS = [
  { id:'rotations', label:'Rotations', icon:<RotateCw size={14} /> },
  { id:'clockwheel', label:'Clock Wheel', icon:<Clock size={14} /> },
  { id:'schedules',  label:'Schedules',  icon:<Calendar size={14} /> },
  { id:'analysis',   label:'Analysis',   icon:<BarChart2 size={14} /> },
];

export default function RotationManager() {
  const [activeTab, setActiveTab] = useState('rotations');
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [loadingStores, setLoadingStores] = useState(true);

  useEffect(() => {
    axios.get(`${API}/tenants/${TENANT}/stores`, { headers: authHeaders() })
      .then(r => {
        const list = r.data?.items || r.data || [];
        setStores(list);
        if (list.length > 0) setStoreId(list[0].id);
      })
      .catch(() => {})
      .finally(() => setLoadingStores(false));
  }, []);

  return (
    <div style={{ padding:'0 0 40px 0' }}>
      {/* Page header */}
      <div style={{ marginBottom:20, display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
            <RotateCw size={20} style={{ color:'var(--accent)' }} />
            <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:'var(--text-primary)' }}>
              Rotation Manager
            </h1>
          </div>
          <p style={{ margin:0, fontSize:13, color:'var(--text-secondary)' }}>
            Manage music categories, clock wheels, broadcast schedules, and spin analysis.
          </p>
        </div>

        {/* Store selector */}
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:12, fontWeight:600, color:'var(--text-muted)',
            textTransform:'uppercase', letterSpacing:'0.06em', whiteSpace:'nowrap' }}>
            Store
          </span>
          {loadingStores ? (
            <span style={{ fontSize:13, color:'var(--text-muted)' }}>Loading…</span>
          ) : (
            <select
              value={storeId}
              onChange={e => setStoreId(e.target.value)}
              style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:7,
                padding:'7px 12px', color:'var(--text-primary)', fontSize:13, minWidth:180, outline:'none',
                cursor:'pointer' }}>
              {stores.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
              {stores.length === 0 && <option value="">No stores found</option>}
            </select>
          )}
        </div>
      </div>

      {/* Require store selection */}
      <TabBar tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {!storeId && !loadingStores ? (
        <div style={{ textAlign:'center', padding:64, color:'var(--text-muted)', fontSize:14,
          border:'1px dashed var(--border)', borderRadius:12 }}>
          No stores configured — add a store first to manage rotations.
        </div>
      ) : (
        <>
          {activeTab === 'rotations'  && <RotationsTab  storeId={storeId} />}
          {activeTab === 'clockwheel' && <ClockWheelTab storeId={storeId} />}
          {activeTab === 'schedules'  && <SchedulesTab  storeId={storeId} />}
          {activeTab === 'analysis'   && <AnalysisTab   storeId={storeId} />}
        </>
      )}

      {/* Keyframe for spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
